import React, { useEffect, useMemo, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import ReactPaginate from "react-paginate";
import { useNavigate } from "react-router-dom";
import { ApiUrl } from "../../../ApiUrl";

const StdHostelDetails = () => {
  const [hostelName, setHostelName] = useState("");
  const [block, setBlock] = useState("");
  const [floorNo, setFloorNo] = useState("");
  const [roomType, setRoomType] = useState("");
  const [roomNo, setRoomNo] = useState("");
  const [bedNo, setBedNo] = useState("");
  const [hostelRows, setHostelRows] = useState([]);
  const [assigned, setAssigned] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(0);

  const navigate = useNavigate();
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchHostelDetails = async () => {
      const studentId = sessionStorage.getItem("userId");
      const academicYearId = localStorage.getItem("academicSessionId") || "";
      const orgId = localStorage.getItem("orgId") || "";
      const branchId = localStorage.getItem("branchId") || "";

      if (!studentId) {
        setError("Student session details are missing. Please log in again.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const queryParams = new URLSearchParams();
        if (academicYearId) queryParams.append("academic_year_id", academicYearId);
        if (orgId) queryParams.append("org_id", orgId);
        if (branchId) queryParams.append("branch_id", branchId);

        const response = await fetch(
          `${ApiUrl.apiurl}STUDENT_HOSTEL/GetStudentHostelSummary/${studentId}?${queryParams.toString()}`
        );

        const contentType = response.headers.get("content-type") || "";
        const payload = contentType.includes("application/json")
          ? await response.json()
          : null;

        if (!response.ok || !payload || payload.message !== "Success") {
          throw new Error(payload?.message || "Unable to load hostel details.");
        }

        const hostelRecord = Array.isArray(payload.data) ? payload.data[0] : null;
        setAssigned(Boolean(payload.assigned));
        setHostelRows(Array.isArray(payload.data) ? payload.data : []);
        setHostelName(hostelRecord?.hostel_name || "");
        setBlock(hostelRecord?.block || "");
        setFloorNo(hostelRecord?.floor_no || "");
        setRoomType(hostelRecord?.room_type || "");
        setRoomNo(hostelRecord?.room_no || "");
        setBedNo(hostelRecord?.bed_no || "");
      } catch (fetchError) {
        console.error("Error fetching hostel details:", fetchError);
        setAssigned(false);
        setHostelRows([]);
        setError(fetchError.message || "Unable to load hostel details.");
      } finally {
        setLoading(false);
      }
    };

    fetchHostelDetails();
  }, []);

  const filteredRows = useMemo(() => {
    const normalize = (value) => (value || "").toString().toLowerCase().trim();

    return hostelRows.filter((row) => {
      if (hostelName && !normalize(row.hostel_name).includes(normalize(hostelName))) {
        return false;
      }
      if (block && !normalize(row.block).includes(normalize(block))) {
        return false;
      }
      if (floorNo && !normalize(row.floor_no).includes(normalize(floorNo))) {
        return false;
      }
      if (roomType && !normalize(row.room_type).includes(normalize(roomType))) {
        return false;
      }
      if (roomNo && !normalize(row.room_no).includes(normalize(roomNo))) {
        return false;
      }
      if (bedNo && !normalize(row.bed_no).includes(normalize(bedNo))) {
        return false;
      }
      return true;
    });
  }, [hostelRows, hostelName, block, floorNo, roomType, roomNo, bedNo]);

  useEffect(() => {
    setCurrentPage(0);
  }, [hostelName, block, floorNo, roomType, roomNo, bedNo]);

  const offset = currentPage * itemsPerPage;
  const currentData = filteredRows.slice(offset, offset + itemsPerPage);
  const pageCount = Math.ceil(filteredRows.length / itemsPerPage);

  const handlePageClick = ({ selected }) => {
    setCurrentPage(selected);
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="card p-0">
            <div className="card-body">
              <p
                style={{
                  marginBottom: "0px",
                  textAlign: "center",
                  fontSize: "20px",
                  fontWeight: "700",
                }}
              >
                HOSTEL DETAILS
              </p>
              <div className="row mb-2 mt-3 mx-0">
                <div className="col-12 d-flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="btn btn-danger me-2"
                    style={{ width: "150px" }}
                    onClick={() => navigate("/student/dashboards")}
                  >
                    Close
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="text-center py-4">Loading hostel details...</div>
              ) : error ? (
                <div className="alert alert-danger mb-0">{error}</div>
              ) : !assigned ? (
                <div className="text-center py-5">
                  <div style={{ fontSize: "24px", fontWeight: "700" }}>
                    No Hostel Assignment
                  </div>
                  <div style={{ marginTop: "12px", fontSize: "16px", color: "#555" }}>
                    You are not currently assigned to any hostel. Please contact the administration for hostel assignment.
                  </div>
                </div>
              ) : (
                <>
                  <div className="row mt-3 mx-2 mt-2">
                    <div className="col-12 custom-section-box">
                      <div className="row flex-grow-1 mb-3 mt-3">
                        <div className="col-12 col-md-3">
                          <label htmlFor="hostel-name" className="form-label">
                            Hostel Name
                          </label>
                          <input
                            type="text"
                            className="form-control detail"
                            placeholder="Enter hostel name"
                            value={hostelName}
                            onChange={(e) => setHostelName(e.target.value)}
                          />
                        </div>

                        <div className="col-12 col-md-3">
                          <label htmlFor="block" className="form-label">
                            Block
                          </label>
                          <input
                            type="text"
                            className="form-control detail"
                            placeholder="Enter block"
                            value={block}
                            onChange={(e) => setBlock(e.target.value)}
                          />
                        </div>

                        <div className="col-12 col-md-3">
                          <label htmlFor="floorNo" className="form-label">
                            Floor Number
                          </label>
                          <input
                            type="number"
                            className="form-control detail"
                            placeholder="Enter floor number"
                            value={floorNo}
                            onChange={(e) => setFloorNo(e.target.value)}
                          />
                        </div>

                        <div className="col-12 col-md-3">
                          <label htmlFor="roomType" className="form-label">
                            Room Type
                          </label>
                          <input
                            type="text"
                            className="form-control detail"
                            placeholder="Enter room type"
                            value={roomType}
                            onChange={(e) => setRoomType(e.target.value)}
                          />
                        </div>

                        <div className="col-12 col-md-3 mt-3">
                          <label htmlFor="roomNo" className="form-label">
                            Room Number
                          </label>
                          <input
                            type="text"
                            className="form-control detail"
                            placeholder="Enter room number"
                            value={roomNo}
                            onChange={(e) => setRoomNo(e.target.value)}
                          />
                        </div>

                        <div className="col-12 col-md-3 mt-3">
                          <label htmlFor="bedNo" className="form-label">
                            Bed Number
                          </label>
                          <input
                            type="text"
                            className="form-control detail"
                            placeholder="Enter bed number"
                            value={bedNo}
                            onChange={(e) => setBedNo(e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="table-responsive">
                      <table className="table table-bordered">
                        <thead>
                          <tr>
                            <th>Sr.No</th>
                            <th>Student Name</th>
                            <th>School Admission No.</th>
                            <th>Class</th>
                            <th>Section</th>
                            <th>Father Name</th>
                            <th>Mother Name</th>
                            <th>Total Hostel Fees</th>
                            <th>Fees paid</th>
                            <th>Balance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentData.length > 0 ? (
                            currentData.map((student, index) => (
                              <tr key={student.student_id}>
                                <td>{offset + index + 1}</td>
                                <td>{student.studentname || "-"}</td>
                                <td>{student.school_admission_no || "-"}</td>
                                <td>{student.className || "-"}</td>
                                <td>{student.sectionName || "-"}</td>
                                <td>{student.fatherName || "-"}</td>
                                <td>{student.motherName || "-"}</td>
                                <td>{student.total_hostel_fees ?? 0}</td>
                                <td>{student.fees_paid ?? 0}</td>
                                <td>{student.balance ?? 0}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="10" className="text-center">
                                No data available
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>

                      {filteredRows.length > itemsPerPage && (
                        <ReactPaginate
                          previousLabel={"Previous"}
                          nextLabel={"Next"}
                          breakLabel={"..."}
                          pageCount={pageCount}
                          onPageChange={handlePageClick}
                          containerClassName={"pagination justify-content-center mt-3"}
                          pageClassName={"page-item"}
                          pageLinkClassName={"page-link"}
                          activeClassName={"active"}
                          previousClassName={"page-item"}
                          previousLinkClassName={"page-link"}
                          nextClassName={"page-item"}
                          nextLinkClassName={"page-link"}
                        />
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StdHostelDetails;
